import { ref, unref } from 'vue'
import axiosInstance from '@/axios'
import { isRequestCancelled, extractSkuFromQuery } from '@/utils/sku-search'

/**
 * useSupplierCatalog — debounced catalog autocomplete for the SELECTED supplier.
 *
 * Hits GET /api/supplier/<key>/catalog?query=<q>&limit=25 with:
 *   - 300ms debounce
 *   - 2-char minimum query length (shorter clears results immediately)
 *   - AbortController + generation counter for in-flight cancellation
 *   - extractSkuFromQuery to clean Vuetify-combobox-style "SKU — Title"
 *     re-emissions before dispatch
 *
 * Public API:
 *   { query, results, loading, error, search(q), settledQuery }
 *
 * Used by Section 1 of the Product editor (hi-fi redesign).
 *
 * WHY THE SUPPLIER IS A PARAMETER
 * -------------------------------
 * This module used to hold a fixed `'/api/supplier/sanmar/catalog'`, so every
 * keystroke asked SanMar regardless of which supplier the picker had selected.
 * Jake hit that on 8083 (2026-09-02): selecting the non-integrated supplier
 * "Coules Labs" gave an empty dropdown, because the request went to SanMar's
 * catalog and SanMar has never heard of his SKUs.
 *
 * Empty was the lucky outcome. The same fixed endpoint pointed at S&S would
 * have returned SanMar's products under S&S's name -- a wrong answer at the
 * first step of adding a product, which then propagates into the store.
 *
 * The key is read through `unref` on every dispatch rather than captured once,
 * so changing the picker changes the next search without re-creating the
 * composable.
 */
const DEBOUNCE_MS = 300
const MIN_QUERY_LEN = 2
const DEFAULT_SUPPLIER_KEY = 'sanmar'
const CATALOG_LIMIT = 25

/** The catalog endpoint for one supplier key. */
function catalogEndpoint(supplierKey) {
  return `/api/supplier/${encodeURIComponent(supplierKey)}/catalog`
}

function friendlyMessage(err) {
  if (!err) return 'Unknown error'
  if (typeof err === 'string') return err
  if (err.message) return err.message
  return 'Catalog search failed'
}

/**
 * @param supplierSource a ref, getter or plain string carrying the selected
 *   supplier key. Defaults to SanMar so an un-migrated caller keeps its old
 *   behavior rather than silently searching nothing.
 */
export function useSupplierCatalog(supplierSource = DEFAULT_SUPPLIER_KEY) {
  const query = ref('')
  // The query the CURRENT `results` answer ('' when none). `results` alone
  // cannot say whether they belong to what was just typed: through the
  // debounce window the previous query's rows stay with loading=false.
  // Automation waits on `settledQuery === query` (SupplierSkuPanel's
  // results-ready sentinel).
  const settledQuery = ref('')
  const results = ref([])
  const loading = ref(false)
  const error = ref(null)

  let debounceTimer = null
  let abortController = null
  let generation = 0

  /** The key to search RIGHT NOW -- re-read per dispatch, never captured. */
  function currentSupplierKey() {
    const raw =
      typeof supplierSource === 'function'
        ? supplierSource()
        : unref(supplierSource)
    return (raw ?? '').toString().trim().toLowerCase()
  }

  async function dispatch(cleanQuery, gen) {
    // No supplier selected. `syncSupplierSelection` in SupplierSkuPanel
    // clears the selection to null when a distributor is entitled to
    // nothing, and a request built on that would ask
    // /api/supplier/null/catalog -- a 404 the user reads as a broken
    // search rather than as "you have not picked a supplier".
    const supplierKey = currentSupplierKey()
    if (!supplierKey) {
      results.value = []
      error.value = null
      loading.value = false
      settledQuery.value = ''
      return
    }

    // Abort any prior in-flight request.
    if (abortController) {
      abortController.abort()
    }
    abortController = new AbortController()
    const localController = abortController

    loading.value = true
    try {
      const response = await axiosInstance.get(catalogEndpoint(supplierKey), {
        params: { query: cleanQuery, limit: CATALOG_LIMIT },
        signal: localController.signal,
      })
      // Discard stale responses (defense in depth alongside AbortController).
      if (gen !== generation) return
      results.value = response.data
      error.value = null
      settledQuery.value = cleanQuery
    } catch (err) {
      // Cancellations are intentional — leave state alone.
      if (isRequestCancelled(err)) return
      if (gen !== generation) return
      // `supplier_catalog_not_loaded`: the S&S catalog table is empty here
      // (a sandbox does not sweep S&S by default). Say so, with the server's
      // own sentence, rather than the bare "status code 503" text.
      const body = err?.response?.data
      if (body?.error === 'supplier_catalog_not_loaded' && body.message) {
        error.value = { message: body.message, catalogNotLoaded: true }
      } else {
        error.value = { message: friendlyMessage(err) }
      }
      results.value = []
      settledQuery.value = ''
    } finally {
      if (gen === generation) {
        loading.value = false
      }
    }
  }

  function search(q) {
    // Kept for forward compatibility with v-combobox in pebble 11 (SKU swap UX).
    // For v-autocomplete the search query is plain text and extractSkuFromQuery
    // is a no-op; cheap defense in depth.
    const cleanQuery = extractSkuFromQuery(q)
    query.value = cleanQuery

    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }

    if (cleanQuery.length < MIN_QUERY_LEN) {
      // Short query: clear immediately AND cancel anything in flight so a
      // late response can't repopulate stale results.
      if (abortController) {
        abortController.abort()
        abortController = null
      }
      generation++
      results.value = []
      error.value = null
      loading.value = false
      settledQuery.value = ''
      return
    }

    const thisGen = ++generation
    debounceTimer = setTimeout(() => {
      debounceTimer = null
      dispatch(cleanQuery, thisGen)
    }, DEBOUNCE_MS)
  }

  return { query, results, loading, error, search, settledQuery }
}

/**
 * Back-compat alias. Kept so a caller that has not been migrated keeps
 * working; it searches SanMar, which is what it always did.
 */
export const useSanmarCatalog = useSupplierCatalog

export default useSupplierCatalog
